/**
 * Pure helpers — apply optimistic updates to the `awsgwAttributesResult`
 * array that the CrewDetailModal (and the rest of the live schedule grid)
 * reads from.
 *
 * Background: when CrewDetailModal is open, ALL polling pauses
 * (`isAnyModalOpen` in AppContext gates the polling queries). TanStack
 * `invalidateQueries` after a mutation does trigger a refetch of the
 * non-polling `getAttributesByLocation` query, but the round-trip is slow
 * relative to user clicks — rapid clicks of the same radio button (or
 * different radios on the same day) all see the same stale
 * `awsgwAttributesResult` and therefore all enter the CREATE branch of
 * `tsUpdateAttributeList`, producing duplicate records.
 *
 * The fix mirrors what `tsCreateCrewDefaultsAttribute` already does (its
 * own comment line 4509 explicitly cites this concern): apply the
 * change to `awsgwAttributesResult` locally BEFORE firing the mutation,
 * so subsequent clicks see the updated state instantly and fall into
 * UPDATE rather than CREATE.
 *
 * Three discrete operations are needed by `tsUpdateAttributeList`:
 *   - CREATE (`applyOptimisticAttributeCreate`)
 *   - UPDATE / REMOVE (`applyOptimisticAttributeFieldsByMongoId`) — both
 *     find by `_id` and merge in `updateFields`; semantically identical
 *
 * Pure functions: no mutation of inputs, always returns a NEW array.
 */

/**
 * Find the document with the given Mongo `_id` in `attributesResult` and
 * return a NEW array where that document's fields are merged with
 * `updateFields`. Used by tsUpdateAttributeList's UPDATE and REMOVE/
 * Available branches.
 *
 * Defensive behavior:
 *   - Returns the array unchanged if `mongoId` is null/undefined (caller
 *     should not have fired this for an optimistic record that doesn't
 *     yet have a real `_id`; we silently no-op rather than corrupt state)
 *   - Returns `[]` if `attributesResult` is not an array
 *   - `_id` comparison via `String(...)` so ObjectId-vs-string mismatches
 *     don't silently fail to find the record
 *
 * @param {Array<object>} attributesResult  the current array (will not be mutated)
 * @param {string|object|null|undefined} mongoId  the `_id` of the target record
 * @param {object} updateFields  the field updates to apply
 * @returns {Array<object>}  a new array with the change applied
 */
export function applyOptimisticAttributeFieldsByMongoId(
  attributesResult,
  mongoId,
  updateFields
) {
  if (!Array.isArray(attributesResult)) return [];
  if (mongoId == null) return attributesResult;
  const targetId = String(mongoId);
  return attributesResult.map((doc) => {
    if (doc == null) return doc;
    const docId = doc._id == null ? null : String(doc._id);
    if (docId === targetId) {
      return { ...doc, ...updateFields };
    }
    return doc;
  });
}

/**
 * Append a newly-created (not yet round-tripped) attribute to the array.
 * The new document typically has no `_id` yet — the mutation's onSuccess
 * is responsible for backfilling the real ObjectId when the server responds.
 *
 * Returns a NEW array.
 *
 * @param {Array<object>} attributesResult  current array (will not be mutated)
 * @param {object} newAttribute  the new document to append
 * @returns {Array<object>}
 */
export function applyOptimisticAttributeCreate(attributesResult, newAttribute) {
  if (!Array.isArray(attributesResult)) return [newAttribute];
  return [...attributesResult, newAttribute];
}

/**
 * Once a CREATE mutation succeeds, backfill the freshly-assigned `_id`
 * into the optimistically-appended document. Caller identifies which
 * optimistic record to patch via the app-side `id` (a uuid generated
 * client-side and used as a stable handle across the create round-trip).
 *
 * Pure; returns a new array.
 *
 * @param {Array<object>} attributesResult
 * @param {string} optimisticIdHandle  the value of newAttribute.id
 * @param {string} insertedMongoId  the ObjectId returned by the Lambda
 * @returns {Array<object>}
 */
export function backfillOptimisticAttributeMongoId(
  attributesResult,
  optimisticIdHandle,
  insertedMongoId
) {
  if (!Array.isArray(attributesResult)) return [];
  if (optimisticIdHandle == null || insertedMongoId == null) {
    return attributesResult;
  }
  return attributesResult.map((doc) => {
    if (doc?.id === optimisticIdHandle && doc?._id == null) {
      return { ...doc, _id: insertedMongoId };
    }
    return doc;
  });
}
